import React from 'react';
import styled from 'styled-components';
import { useMavrkPools, PoolWithTVL } from '../../hooks/useMavrkPools';
import { useRealtimePrices, CHAIN_IDS } from '../../hooks/useRealtimePrices';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  gap: 8px;
`;

const Header = styled.div`
  padding: 12px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  margin-bottom: 8px;
`;

const Title = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #ffffff;
  margin: 0 0 4px 0;
  font-family: 'Plus Jakarta Sans', sans-serif;
`;

const Subtitle = styled.p`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
  margin: 0;
  font-family: 'Plus Jakarta Sans', sans-serif;
`;

const PoolsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 500px;
  overflow-y: auto;
  flex: 1;
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 3px;
    
    &:hover {
      background: rgba(255, 255, 255, 0.3);
    }
  }
`;

const PoolCard = styled.div`
  padding: 12px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.05);
    border-color: rgba(255, 255, 255, 0.1);
  }
`;

const PoolRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const PoolInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  min-width: 0;
`;

const TokenLogo = styled.img`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
  background: rgba(255, 255, 255, 0.05);
`;

const TokenDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1;
`;

const TokenName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #ffffff;
  font-family: 'Plus Jakarta Sans', sans-serif;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const TokenSymbol = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
  font-family: 'Plus Jakarta Sans', sans-serif;
`;

const PoolMeta = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
  flex-shrink: 0;
`;

const PoolAddressShort = styled.div`
  font-size: 10px;
  color: rgba(255, 255, 255, 0.4);
  font-family: 'Courier New', monospace;
`;

const TokenPrice = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  font-family: 'Plus Jakarta Sans', sans-serif;
  
  &.no-price {
    color: rgba(255, 255, 255, 0.4);
    font-weight: 400;
  }
`;

const NFTBadge = styled.div`
  font-size: 10px;
  color: #60a5fa;
  font-family: 'Plus Jakarta Sans', sans-serif;
`;

const LoadingState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 200px;
  color: rgba(255, 255, 255, 0.5);
  font-family: 'Plus Jakarta Sans', sans-serif;
`;

const ErrorState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  gap: 12px;
`;

const ErrorMessage = styled.div`
  color: #ef4444;
  font-family: 'Plus Jakarta Sans', sans-serif;
`;

const RetryButton = styled.button`
  padding: 8px 16px;
  background: #3b82f6;
  color: #ffffff;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 14px;
  transition: background 0.2s ease;
  
  &:hover {
    background: #2563eb;
  }
`;

interface MavrkPoolsListProps {
  onPoolSelect?: (pool: PoolWithTVL) => void;
}

export const MavrkPoolsList: React.FC<MavrkPoolsListProps> = ({ onPoolSelect }) => {
  const { pools, loading, error, refetch } = useMavrkPools();
  const { getPriceForToken } = useRealtimePrices(CHAIN_IDS.INK);

  if (loading) {
    return (
      <LoadingState>Loading pools...</LoadingState>
    );
  }

  if (error) {
    return (
      <ErrorState>
        <ErrorMessage>Error: {error}</ErrorMessage>
        <RetryButton onClick={refetch}>Retry</RetryButton>
      </ErrorState>
    );
  }

  if (pools.length === 0) {
    return (
      <LoadingState>No pools found</LoadingState>
    );
  }

  return (
    <Container>
      <Header>
        <Title>Mavrk Pools</Title>
        <Subtitle>{pools.length} pool{pools.length !== 1 ? 's' : ''}</Subtitle>
      </Header>
      
      <PoolsList>
        {pools.map((pool) => (
          <PoolCard
            key={pool.contract_address}
            onClick={() => onPoolSelect?.(pool)}
          >
            <PoolRow>
              <PoolInfo>
                {pool.logo_url && (
                  <TokenLogo
                    src={pool.logo_url}
                    alt={pool.symbol}
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = '/token-placeholder.png';
                    }}
                  />
                )}
                <TokenDetails>
                  <TokenName>{pool.name}</TokenName>
                  <TokenSymbol>{pool.symbol}</TokenSymbol>
                </TokenDetails>
              </PoolInfo>
              
              <PoolMeta>
                {(() => {
                  const price = getPriceForToken(pool.contract_address, CHAIN_IDS.INK);
                  return (
                    <>
                      {price ? (
                        <TokenPrice>
                          ${price.toLocaleString(undefined, {
                            minimumFractionDigits: price < 0.01 ? 6 : 2,
                            maximumFractionDigits: price < 0.01 ? 6 : 2
                          })}
                        </TokenPrice>
                      ) : (
                        <TokenPrice className="no-price">--</TokenPrice>
                      )}
                      {pool.pool_address && (
                        <PoolAddressShort>
                          {pool.pool_address.slice(0, 6)}...{pool.pool_address.slice(-4)}
                        </PoolAddressShort>
                      )}
                      {pool.nft_token_id && (
                        <NFTBadge>NFT: #{pool.nft_token_id}</NFTBadge>
                      )}
                    </>
                  );
                })()}
              </PoolMeta>
            </PoolRow>
          </PoolCard>
        ))}
      </PoolsList>
    </Container>
  );
};

